'use client';

import Image from '@/components/Image';
import { env } from '@/utils/env';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { StyleHotProductCategoryProduct, StyleHotProductRawAdCategory } from '../types';
import { getInitialStyleCategoryTabIndex } from '../utils';
import {
  getStyleHotProductColorStateKey,
  registerStyleHotProductPageReturnScrollRestore,
  type StyleHotProductViewState,
} from '../hooks/styleHotProductViewState';
import { useStyleCategoryProducts } from '../hooks/useStyleCategoryProducts';
import { useStyleHotProductViewState } from '../hooks/useStyleHotProductViewState';
import StyleCategorySwiper from './StyleCategorySwiper';
import StyleProductList from './StyleProductList';
import StyleProductVenueWap from './StyleCategoryWap/StyleProductVenueWap';

interface StyleCategoryProps {
  deviceType: EPlatform;
  data?: StyleHotProductRawAdCategory | null;
  marketingPageId?: number;
  adp?: string;
  isPreview?: boolean;
  inAppH5?: boolean;
  styleJumpRequest?: {
    styleName: string;
    requestId: number;
  } | null;
  onTrackNavTabClick?: (tabName: string, index: number, moduleName?: string) => void;
  onTrackProductClick?: (
    item: StyleHotProductCategoryProduct,
    index: number,
    moduleName: string,
    tabName?: string,
  ) => void;
  onTrackProductExposure?: (
    item: StyleHotProductCategoryProduct,
    index: number,
    moduleName: string,
    tabName?: string,
  ) => void;
}

const PC_STICKY_TOP_OFFSET = 100;
const PC_STICKY_BAR_HEIGHT = 88;
const PC_STICKY_SCROLL_LOCK_TIMEOUT = 1200;
const PC_STICKY_VISIBILITY_TOLERANCE = 2;
const STYLE_CATEGORY_SWIPER_MODULE_NAME = '商品分类版位-a';
const STYLE_CATEGORY_STICKY_MODULE_NAME = '商品分类版位-b';
const STYLE_CATEGORY_PRODUCT_MODULE_NAME = '商品分类版位';

export interface StyleCategoryCacheProps {
  activeTabIndex: number;
  viewState: StyleHotProductViewState;
  restoredFromCache: boolean;
  updateViewState: (
    patch: Partial<StyleHotProductViewState>,
  ) => Promise<StyleHotProductViewState | undefined>;
}

function resolveAssetUrl(path?: string | null): string {
  if (!path) {
    return '';
  }

  if (/^(https?:)?\/\//.test(path) || path.startsWith('blob:')) {
    return path;
  }

  if (path.startsWith('/admin/')) {
    return `${env.NEXT_PUBLIC_S3_URL}${path}`;
  }

  return path;
}

function normalizeStyleName(styleName?: string | null): string {
  return (styleName || '').trim().toLowerCase();
}

function getStyleCategoryTabName(tab?: StyleHotProductRawAdCategory['tabs'][number]): string {
  return tab?.tab_title || tab?.tab_name || tab?.title || '';
}

interface StyleStickyTabsProps {
  tabs: NonNullable<StyleHotProductRawAdCategory['tabs']>;
  activeTabIndex: number;
  isVisible: boolean;
  onTabClick: (index: number) => void;
}

function StyleStickyTabs({ tabs, activeTabIndex, isVisible, onTabClick }: StyleStickyTabsProps) {
  if (!isVisible || tabs.length === 0) {
    return null;
  }

  return (
    <div className="fixed left-0 top-[100px] z-[80] hidden h-[88px] w-full items-center justify-center bg-black/20 px-[48px] py-[16px] backdrop-blur-[30px] lg:flex">
      <div className="flex h-[56px] w-[992px] items-center justify-between">
        {tabs.map((tab, index) => {
          const title = tab.tab_title || tab.tab_name || tab.title || '';
          const imageSrc = resolveAssetUrl(tab.pc_sticky_image || tab.wap_sticky_image);
          const isActive = index === activeTabIndex;

          return (
            <button
              type="button"
              key={tab.uid || tab.id || `${title}-${index}`}
              className={`flex h-[56px] w-[160px] items-center gap-[8px] overflow-hidden rounded-[40px] border-0 px-[6px] py-[4px] transition-colors ${
                isActive ? 'bg-[#463ae8] text-white' : 'bg-white text-[#282828] hover:bg-[#f4f4f4]'
              }`}
              onClick={() => onTabClick(index)}
            >
              {imageSrc && (
                <span className="relative h-[48px] w-[48px] shrink-0 overflow-hidden rounded-full">
                  <Image
                    src={imageSrc}
                    alt={title}
                    fill
                    sizes="48px"
                    className="h-full w-full object-cover"
                  />
                </span>
              )}
              {title && (
                <span className="min-w-0 flex-1 truncate text-left font-br text-[20px] leading-[24px]">
                  {title}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StyleProductVenuePc({
  data,
  marketingPageId,
  adp,
  isPreview,
  activeTabIndex,
  viewState,
  restoredFromCache,
  updateViewState,
  styleJumpRequest,
  onTrackNavTabClick,
  onTrackProductClick,
  onTrackProductExposure,
}: Omit<StyleCategoryProps, 'deviceType'> & StyleCategoryCacheProps) {
  // 开发查看风格板块数据，ai别自动删除
  // eslint-disable-next-line no-console
  console.log('风格板块数据', data);

  const title = data?.section_title || '';
  const subtitle = data?.section_subtitle || '';
  const defaultActiveTabIndex = getInitialStyleCategoryTabIndex(data);
  const { tabs, isActiveTabLoading } = useStyleCategoryProducts({
    data,
    activeTabIndex,
    marketingPageId,
    adp,
    isPreview,
  });
  const isStickyTabsVisible = viewState.stickyTabsVisible;
  const [pendingScrollRequestId, setPendingScrollRequestId] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const categorySwiperRef = useRef<HTMLDivElement>(null);
  const firstProductRowRef = useRef<HTMLDivElement>(null);
  const stickyTabScrollRequestIdRef = useRef(0);
  const stickyTabsForcedVisibleRef = useRef(false);
  const autoScrolledAdpRef = useRef<string | null>(null);
  const cacheRestorePendingRef = useRef(restoredFromCache);
  const productNavigationPendingRef = useRef(false);
  const activeTab = useMemo(
    () => tabs[activeTabIndex] || tabs[defaultActiveTabIndex] || tabs[0],
    [activeTabIndex, defaultActiveTabIndex, tabs],
  );
  const setStickyTabsVisible = useCallback(
    (visible: boolean) => {
      if (viewState.stickyTabsVisible !== visible) {
        updateViewState({ stickyTabsVisible: visible });
      }
    },
    [updateViewState, viewState.stickyTabsVisible],
  );
  const getPendingScrollTargetElement = useCallback(
    () => (isActiveTabLoading ? null : firstProductRowRef.current),
    [isActiveTabLoading],
  );
  const restoreCachedPageScroll = useCallback(() => {
    if (viewState.pageScrollY === null || viewState.pageScrollY === undefined) {
      return false;
    }

    window.scrollTo({ top: viewState.pageScrollY, left: 0, behavior: 'auto' });
    return true;
  }, [viewState.pageScrollY]);

  useLayoutEffect(() => {
    restoreCachedPageScroll();
  }, [restoreCachedPageScroll]);

  const syncStickyTabsVisibility = useCallback(() => {
    const sectionEl = sectionRef.current;

    if (!sectionEl || window.innerWidth < 1024) {
      stickyTabsForcedVisibleRef.current = false;
      setStickyTabsVisible(false);
      return;
    }

    const sectionRect = sectionEl.getBoundingClientRect();

    if (stickyTabsForcedVisibleRef.current) {
      const shouldKeepVisible = sectionRect.bottom > PC_STICKY_TOP_OFFSET + PC_STICKY_BAR_HEIGHT;

      setStickyTabsVisible(shouldKeepVisible);
      return;
    }

    const firstProductRowEl = firstProductRowRef.current;

    if (!firstProductRowEl) {
      setStickyTabsVisible(false);
      return;
    }

    const firstProductRowRect = firstProductRowEl.getBoundingClientRect();
    const shouldShow =
      firstProductRowRect.top <= PC_STICKY_TOP_OFFSET + PC_STICKY_VISIBILITY_TOLERANCE &&
      sectionRect.bottom > PC_STICKY_TOP_OFFSET + PC_STICKY_BAR_HEIGHT;

    setStickyTabsVisible(shouldShow);
  }, [setStickyTabsVisible]);

  useEffect(() => {
    let animationFrameId = 0;
    let restoreFrameId = 0;

    const scheduleCachedScrollRestore = () => {
      if (!restoreCachedPageScroll()) {
        return false;
      }

      cacheRestorePendingRef.current = true;

      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (restoreFrameId) {
        window.cancelAnimationFrame(restoreFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        restoreCachedPageScroll();
        restoreFrameId = window.requestAnimationFrame(() => {
          restoreCachedPageScroll();
          animationFrameId = 0;
          restoreFrameId = 0;
          cacheRestorePendingRef.current = false;
        });
      });

      return true;
    };

    const requestSync = () => {
      if (
        animationFrameId ||
        cacheRestorePendingRef.current ||
        productNavigationPendingRef.current
      ) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = 0;
        syncStickyTabsVisibility();
      });
    };

    if (cacheRestorePendingRef.current) {
      if (!scheduleCachedScrollRestore()) {
        cacheRestorePendingRef.current = false;
        syncStickyTabsVisibility();
      }
    } else {
      syncStickyTabsVisibility();
    }
    window.addEventListener('scroll', requestSync, { passive: true });
    window.addEventListener('resize', requestSync);
    window.addEventListener('pageshow', scheduleCachedScrollRestore);
    window.addEventListener('popstate', scheduleCachedScrollRestore);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (restoreFrameId) {
        window.cancelAnimationFrame(restoreFrameId);
      }

      window.removeEventListener('scroll', requestSync);
      window.removeEventListener('resize', requestSync);
      window.removeEventListener('pageshow', scheduleCachedScrollRestore);
      window.removeEventListener('popstate', scheduleCachedScrollRestore);
    };
  }, [restoreCachedPageScroll, syncStickyTabsVisibility]);

  useEffect(() => {
    if (!cacheRestorePendingRef.current) {
      syncStickyTabsVisibility();
    }
  }, [activeTabIndex, syncStickyTabsVisibility]);

  useEffect(() => {
    if (!styleJumpRequest?.styleName) {
      return;
    }

    const targetStyleName = normalizeStyleName(styleJumpRequest.styleName);
    const targetTabIndex = tabs.findIndex((tab) => {
      const tabStyleName = normalizeStyleName(tab.tab_title || tab.tab_name || tab.title);

      return tabStyleName === targetStyleName;
    });

    if (targetTabIndex < 0) {
      return;
    }

    updateViewState({ activeTabIndex: targetTabIndex });
    setPendingScrollRequestId(styleJumpRequest.requestId);
    stickyTabsForcedVisibleRef.current = true;

    if (window.innerWidth >= 1024) {
      setStickyTabsVisible(true);
    }
  }, [setStickyTabsVisible, styleJumpRequest, tabs, updateViewState]);

  useEffect(() => {
    if (pendingScrollRequestId === null) {
      return;
    }

    let firstAnimationFrameId = 0;
    let secondAnimationFrameId = 0;
    let unlockTimeoutId = 0;
    let hasReleasedStickyLock = false;
    let releaseStickyLock: (() => void) | null = null;

    firstAnimationFrameId = window.requestAnimationFrame(() => {
      secondAnimationFrameId = window.requestAnimationFrame(() => {
        const scrollTargetEl = getPendingScrollTargetElement();

        if (!scrollTargetEl) {
          if (isActiveTabLoading) {
            return;
          }

          stickyTabsForcedVisibleRef.current = false;
          setPendingScrollRequestId(null);
          return;
        }

        stickyTabsForcedVisibleRef.current = window.innerWidth >= 1024;
        const targetTop =
          scrollTargetEl.getBoundingClientRect().top + window.scrollY - PC_STICKY_TOP_OFFSET;

        window.scrollTo({
          top: Math.max(targetTop, 0),
          behavior: 'smooth',
        });

        if (window.innerWidth >= 1024) {
          setStickyTabsVisible(true);
        }

        releaseStickyLock = () => {
          if (hasReleasedStickyLock) {
            return;
          }

          hasReleasedStickyLock = true;
          stickyTabsForcedVisibleRef.current = false;
          setPendingScrollRequestId(null);
          window.requestAnimationFrame(syncStickyTabsVisibility);
        };

        window.addEventListener('scrollend', releaseStickyLock, { once: true });
        unlockTimeoutId = window.setTimeout(releaseStickyLock, PC_STICKY_SCROLL_LOCK_TIMEOUT);
      });
    });

    return () => {
      if (firstAnimationFrameId) {
        window.cancelAnimationFrame(firstAnimationFrameId);
      }

      if (secondAnimationFrameId) {
        window.cancelAnimationFrame(secondAnimationFrameId);
      }

      if (releaseStickyLock) {
        window.removeEventListener('scrollend', releaseStickyLock);
      }

      if (unlockTimeoutId) {
        window.clearTimeout(unlockTimeoutId);
      }
    };
  }, [
    activeTabIndex,
    getPendingScrollTargetElement,
    isActiveTabLoading,
    pendingScrollRequestId,
    setStickyTabsVisible,
    syncStickyTabsVisibility,
  ]);

  const handleTabChange = useCallback(
    (index: number) => {
      if (index === activeTabIndex) {
        return;
      }

      updateViewState({ activeTabIndex: index });
      onTrackNavTabClick?.(
        getStyleCategoryTabName(tabs[index]),
        index,
        STYLE_CATEGORY_SWIPER_MODULE_NAME,
      );
    },
    [activeTabIndex, onTrackNavTabClick, tabs, updateViewState],
  );

  const handleStickyTabClick = useCallback(
    (index: number) => {
      onTrackNavTabClick?.(
        getStyleCategoryTabName(tabs[index]),
        index,
        STYLE_CATEGORY_STICKY_MODULE_NAME,
      );

      stickyTabScrollRequestIdRef.current += 1;
      stickyTabsForcedVisibleRef.current = true;
      updateViewState({ activeTabIndex: index, stickyTabsVisible: true });
      setPendingScrollRequestId(stickyTabScrollRequestIdRef.current);

      if (window.innerWidth >= 1024) {
        setStickyTabsVisible(true);
      }
    },
    [onTrackNavTabClick, setStickyTabsVisible, tabs, updateViewState],
  );

  const handleCategorySwiperUserNavigate = useCallback((swiperEl: HTMLElement) => {
    if (window.innerWidth < 1024) {
      return;
    }

    const swiperRect = swiperEl.getBoundingClientRect();
    const targetTop = swiperRect.top + window.scrollY - PC_STICKY_TOP_OFFSET;

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    if (
      !adp ||
      tabs.length === 0 ||
      pendingScrollRequestId !== null ||
      autoScrolledAdpRef.current === adp ||
      restoredFromCache
    ) {
      return;
    }

    const swiperEl = categorySwiperRef.current;

    if (!swiperEl) {
      return;
    }

    let secondAnimationFrameId = 0;
    const firstAnimationFrameId = window.requestAnimationFrame(() => {
      secondAnimationFrameId = window.requestAnimationFrame(() => {
        autoScrolledAdpRef.current = adp;
        handleCategorySwiperUserNavigate(swiperEl);
      });
    });

    return () => {
      window.cancelAnimationFrame(firstAnimationFrameId);
      if (secondAnimationFrameId) {
        window.cancelAnimationFrame(secondAnimationFrameId);
      }
    };
  }, [
    adp,
    handleCategorySwiperUserNavigate,
    pendingScrollRequestId,
    restoredFromCache,
    tabs.length,
  ]);

  const handleProductClick = useCallback(
    async (item: StyleHotProductCategoryProduct, index: number) => {
      const pageScrollY = window.scrollY;
      const pageUrl = `${window.location.pathname}${window.location.search}`;

      productNavigationPendingRef.current = true;
      await updateViewState({
        pageScrollY,
        stickyTabsVisible: isStickyTabsVisible,
      });
      registerStyleHotProductPageReturnScrollRestore({
        pageUrl,
        scrollY: pageScrollY,
        onRestored: () => {
          productNavigationPendingRef.current = false;
        },
      });
      onTrackProductClick?.(
        item,
        index,
        STYLE_CATEGORY_PRODUCT_MODULE_NAME,
        getStyleCategoryTabName(activeTab),
      );
    },
    [activeTab, isStickyTabsVisible, onTrackProductClick, updateViewState],
  );
  const handleProductColorChange = useCallback(
    (item: StyleHotProductCategoryProduct, colorIndex: number) => {
      const productKey = getStyleHotProductColorStateKey(item);

      if (!productKey || viewState.selectedColorByProductKey[productKey] === colorIndex) {
        return;
      }

      updateViewState({
        selectedColorByProductKey: {
          ...viewState.selectedColorByProductKey,
          [productKey]: colorIndex,
        },
      });
    },
    [updateViewState, viewState.selectedColorByProductKey],
  );

  return (
    <section
      ref={sectionRef}
      className="w-full overflow-hidden bg-black px-[48px] pt-[80px] text-white"
    >
      <div className="mx-auto flex max-w-[1824px] flex-col">
        <div className="mb-[82px] flex h-[115px] items-center justify-between gap-[48px]">
          {(title || subtitle) && (
            <>
              {subtitle && (
                <p className="line-clamp-4 w-[395px] shrink-0 font-br text-[16px] leading-[19px] text-white">
                  {subtitle}
                </p>
              )}
              {title && (
                <h2 className="min-w-0 flex-1 truncate text-right font-br text-[36px] uppercase leading-[1] text-white xl:text-[56px] 2xl:text-[64px] min-[1920px]:text-[80px]">
                  {title}
                </h2>
              )}
            </>
          )}
        </div>

        <div className="relative left-1/2 w-screen -translate-x-1/2 bg-[linear-gradient(180deg,#000_0%,#404040_100%)]">
          <div className="mx-auto w-full max-w-[1920px] px-[48px]">
            <div ref={categorySwiperRef}>
              <StyleCategorySwiper
                tabs={tabs}
                activeTabIndex={activeTabIndex}
                onActiveTabChange={handleTabChange}
                onUserNavigate={handleCategorySwiperUserNavigate}
              />
            </div>
            <div className="h-[82px] w-full" aria-hidden="true" />
          </div>
        </div>
        <div className="relative left-1/2 w-screen -translate-x-1/2 bg-[linear-gradient(180deg,#404040_0%,#000_100%)]">
          <div className="mx-auto w-full max-w-[1920px] px-[48px] pb-[100px]">
            <StyleProductList
              products={activeTab?.products}
              tabName={getStyleCategoryTabName(activeTab)}
              firstRowRef={firstProductRowRef}
              isLoading={isActiveTabLoading}
              selectedColorByProductKey={viewState.selectedColorByProductKey}
              onProductClick={handleProductClick}
              onProductColorChange={handleProductColorChange}
              onProductExposure={(item, index) =>
                onTrackProductExposure?.(
                  item,
                  index,
                  STYLE_CATEGORY_PRODUCT_MODULE_NAME,
                  getStyleCategoryTabName(activeTab),
                )
              }
            />
          </div>
        </div>
      </div>
      {isStickyTabsVisible && (
        <StyleStickyTabs
          tabs={tabs}
          activeTabIndex={activeTabIndex}
          isVisible={isStickyTabsVisible}
          onTabClick={handleStickyTabClick}
        />
      )}
    </section>
  );
}

export default function StyleCategory({
  deviceType,
  data,
  marketingPageId,
  adp,
  isPreview,
  inAppH5,
  styleJumpRequest,
  onTrackNavTabClick,
  onTrackProductClick,
  onTrackProductExposure,
}: StyleCategoryProps) {
  const defaultActiveTabIndex = getInitialStyleCategoryTabIndex(data);
  const { viewState, updateViewState, restoredFromCache } = useStyleHotProductViewState({
    marketingPageId,
    isPreview: Boolean(isPreview),
    adp,
    deviceType,
    defaultActiveTabIndex,
  });
  const activeTabIndex = data?.tabs?.[viewState.activeTabIndex]
    ? viewState.activeTabIndex
    : defaultActiveTabIndex;

  useEffect(() => {
    if (activeTabIndex !== viewState.activeTabIndex) {
      updateViewState({ activeTabIndex });
    }
  }, [activeTabIndex, updateViewState, viewState.activeTabIndex]);

  const cacheProps: StyleCategoryCacheProps = {
    activeTabIndex,
    viewState,
    restoredFromCache,
    updateViewState,
  };

  if (deviceType !== EPlatform.WEB) {
    return (
      <StyleProductVenueWap
        data={data}
        marketingPageId={marketingPageId}
        adp={adp}
        isPreview={isPreview}
        inAppH5={inAppH5}
        {...cacheProps}
        styleJumpRequest={styleJumpRequest}
        onTrackNavTabClick={onTrackNavTabClick}
        onTrackProductClick={onTrackProductClick}
        onTrackProductExposure={onTrackProductExposure}
      />
    );
  }

  return (
    <StyleProductVenuePc
      data={data}
      marketingPageId={marketingPageId}
      adp={adp}
      isPreview={isPreview}
      {...cacheProps}
      styleJumpRequest={styleJumpRequest}
      onTrackNavTabClick={onTrackNavTabClick}
      onTrackProductClick={onTrackProductClick}
      onTrackProductExposure={onTrackProductExposure}
    />
  );
}
